import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useMemo} from 'react';
import {Node, XYPosition} from 'reactflow';
import {v4 as uuidv4} from 'uuid';

import isArchived from 'archive/isArchived';
import {
  ProcessExecutionType,
  ProcessFlowchartNodeDetailsType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import notifications from 'notifications';
import {createTaskNode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/nodeUtils';
import {NodeCreationContext} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartNodeAdapter';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';
import {isFlowchartTaskSchema} from 'processes/processes-platform/isFlowchartTaskSchema';
import {BenchlingNodeV2} from 'ui-library/reactflow/nodes/BenchlingNode/v2/BenchlingNode';

import {ProcessTaskSchemaGraphQL} from '../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {generateTaskSchemaNodeIdentifier} from '../flowchartIdentifierUtils';
import {
  TaskDefinitionVersionSpecWithTaskSchema,
  TaskSchemaNodeData,
} from '../processFlowchartConfigEditorNodeTypes';
import {
  createStaticNodeDataFromTaskDefinitionVersion,
  createStaticNodeDataFromTaskDefinitionVersionSpec,
  createInteractiveNodeFromStaticData,
  FlowchartOptions,
} from '../processFlowchartConfigEditorUtils';
import {SingletonTaskSchemasContext} from '../SingletonTaskSchemasContext';
import {ConfigurableTaskDefinitionGQL} from '../useAllSchemasById';
import {NodeSidepanelSectionProps} from './NodeSidepanelSection';
import {DraggableNode} from './sidepanelNodeUtils';

function DraggableTaskNode(props: {
  taskDefinition: ConfigurableTaskDefinitionGQL;
  nodeId: string;
  addNode: (node: Node<TaskSchemaNodeData>) => void;
  options: FlowchartOptions;
}): JSX.Element {
  const staticNodeData = useMemo(
    () =>
      createStaticNodeDataFromTaskDefinitionVersion({
        nodeClientId: props.nodeId,
        taskDefinitionVersion: props.taskDefinition.latestVersion,
        nodeDetailsType: ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS,
        allTaskSchemas: [props.taskDefinition.latestVersion.processTaskSchema],
      }),
    [props.nodeId, props.taskDefinition.latestVersion]
  );

  const addTaskNode = useCallback(
    (position: XYPosition) => {
      const newNode = createTaskNode(staticNodeData, position, props.options);
      props.addNode(newNode);
    },
    [props, staticNodeData]
  );

  const nodeRenderer = useCallback(
    ({dragging}: {dragging: boolean}) => (
      <BenchlingNodeV2.Standard.Node
        headerProps={{
          title: staticNodeData.nodeName,
          // TODO: Refactor this once staticNodeData is shifted to work with StandardNode
          leftIcon:
            typeof staticNodeData.nodeIcon === 'string'
              ? staticNodeData.nodeIcon
              : staticNodeData.nodeIcon?.icon,
        }}
      />
    ),
    [staticNodeData.nodeName, staticNodeData.nodeIcon]
  );

  return <DraggableNode addNode={addTaskNode} nodeRenderer={nodeRenderer} />;
}

function DraggableCreateNode(props: {
  executionType: ProcessExecutionType;
  addNodeToCanvas: (node: Node<TaskSchemaNodeData>) => void;
  options: FlowchartOptions;
  rootTaskSchema: ProcessTaskSchemaGraphQL;
}): JSX.Element {
  const singletonTaskSchemasByName = SingletonTaskSchemasContext.useContext();

  const {adapter, nodeCreationContext} = useMemo(() => {
    const adapter = PLUGIN_REGISTRY_INSTANCE.getNodeAdapter(props.executionType);

    // For some execution types, we need to use a singleton task schema.
    // If the adapter has no singleton task schema, we'll likely create a new task schema of the execution type.
    const maybeSingletonTaskSchemaName = adapter.getSingletonTaskSchemaName(props.rootTaskSchema);
    const maybeSingletonTaskSchema = maybeSingletonTaskSchemaName
      ? (singletonTaskSchemasByName[maybeSingletonTaskSchemaName] ?? null)
      : null;

    const nodeCreationContext: NodeCreationContext = {
      rootTaskSchema: props.rootTaskSchema,
      singletonTaskSchemaForExecutionType: maybeSingletonTaskSchema,
    };

    return {
      adapter,
      nodeCreationContext,
    };
  }, [props.executionType, props.rootTaskSchema, singletonTaskSchemasByName]);

  const {nodeName, nodeSubtitle, nodeIcon} = useMemo(
    () => adapter.getNewNodeDetails(nodeCreationContext),
    [adapter, nodeCreationContext]
  );

  const handleCreateNode = useCallback(
    async (position: XYPosition) => {
      let taskDefinitionVersionSpec: TaskDefinitionVersionSpecWithTaskSchema | null = null;
      try {
        taskDefinitionVersionSpec = await adapter.onCreateNewNode(nodeCreationContext);
        // eslint-disable-next-line benchlint/always-throw-from-catch
      } catch (error) {
        notifications.error(
          'Error creating node, please reach out to support@benchling.com for more information.'
        );
        return;
      }

      if (!taskDefinitionVersionSpec) {
        // If taskDefinitionVersionSpec is null, user cancelled creation - don't add node or show error
        return;
      }

      const nodeId = uuidv4();

      const staticNodeData = createStaticNodeDataFromTaskDefinitionVersionSpec({
        nodeClientId: nodeId,
        taskDefinitionVersionSpec,
        taskSchemaId: taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchemaId,
        nodeDetailsType: ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS,
      });

      const newNode = createInteractiveNodeFromStaticData(staticNodeData, position, props.options);

      props.addNodeToCanvas(newNode);
    },
    [props, adapter, nodeCreationContext]
  );

  const nodeRenderer = useCallback(
    () => (
      <BenchlingNodeV2.Standard.Node
        headerProps={{
          title: nodeName,
          subTitle: nodeSubtitle ?? undefined,
          leftIcon: nodeIcon ?? undefined,
        }}
      />
    ),
    [nodeName, nodeSubtitle, nodeIcon]
  );

  return <DraggableNode addNode={handleCreateNode} nodeRenderer={nodeRenderer} />;
}

/**
 * A sidepanel component that displays all available task nodes that can be added to the flowchart
 */
export default function TaskNodeSidepanelSection(
  props: Omit<NodeSidepanelSectionProps, 'section' | 'nodeTypes'>
): JSX.Element {
  const allowedExecutionTypes = props.flowchartEditorConfigurator.allowedNodeExecutionTypes;

  // Filtered task definitions - show only latest version of each task definition
  const filteredTaskDefinitions = Array.from(
    props.taskDefinitionsForSidepanel.filter(
      (definition) =>
        !isArchived(definition.latestVersion.processTaskSchema) &&
        !isFlowchartTaskSchema(definition.latestVersion.processTaskSchema) &&
        (props.searchQuery == null ||
          definition.latestVersion.processTaskSchema.name
            .toLowerCase()
            .includes(props.searchQuery.toLowerCase()))
    )
  );

  return (
    <div className={css(styles.taskNodesContainer)}>
      {/* Create New Nodes Section */}
      {allowedExecutionTypes
        .filter((executionType) => !isFlowchartTaskSchema({executionType}))
        .map((executionType) => (
          <DraggableCreateNode
            key={`create-${executionType}`}
            executionType={executionType}
            addNodeToCanvas={props.addNodeToCanvas}
            options={props.options}
            rootTaskSchema={props.rootTaskSchema}
          />
        ))}

      {allowedExecutionTypes.length > 0 && filteredTaskDefinitions.length > 0 && (
        <div className={css(styles.separator)} />
      )}

      {/* Existing Task Definitions */}
      {filteredTaskDefinitions.map((definition) => {
        const nodeId = generateTaskSchemaNodeIdentifier(definition.latestVersion.processTaskSchema);
        return (
          <DraggableTaskNode
            key={nodeId}
            nodeId={nodeId}
            taskDefinition={definition}
            addNode={props.addNodeToCanvas}
            options={props.options}
          />
        );
      })}
    </div>
  );
}

const styles = StyleSheet.create({
  taskNodesContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
  separator: {
    width: '100%',
    height: '1px',
    backgroundColor: '#eee',
    margin: '10px 0',
  },
});
